import React, { FC, useCallback } from "react";
import { connect } from "react-redux";

import { faDownload } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import { Territory } from "@mapmycustomers/shared/types/entity";
import { Popconfirm } from "@mapmycustomers/ui";

import AddOrCreateButton from "@app/component/preview/components/AddOrCreateButton";
import MapMode from "@app/scene/map/enums/MapMode";
import { getMapMode } from "@app/scene/map/store/selectors";
import { RootState } from "@app/store/rootReducer";
import { TOPMOST_MODAL_ZINDEX } from "@app/util/consts";
import useAnalytics from "@app/util/contexts/useAnalytics";
import downloadTerritoryBoundsAsCsv from "@app/util/downloadTerritoryBoundsAsCsv";

import styles from "./Header.module.scss";
import messages from "./messages";

export interface HeaderProps {
  canCreateTerritories?: boolean;
  onCreateTerritory?: () => void;
  territories: Territory[];
  total: number;
}

interface Props extends HeaderProps {
  mode?: MapMode;
}

const Header: FC<Props> = ({
  canCreateTerritories,
  mode,
  onCreateTerritory,
  territories,
  total,
}) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const showCreateConfirmation = [MapMode.GROUPS, MapMode.TERRITORIES].includes(mode!);

  const handleDownloadTerritoryBounds = useCallback(() => {
    downloadTerritoryBoundsAsCsv(intl, territories);
    analytics.clicked(["Download All Territory Bounds"]);
  }, [analytics, intl, territories]);

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        <span>{intl.formatMessage(messages.title, { count: territories.length, total })}</span>
        <Popconfirm
          description={intl.formatMessage(messages.popoverContent)}
          okText={intl.formatMessage(messages.okText)}
          onConfirm={handleDownloadTerritoryBounds}
          overlayClassName={styles.downloadPopconfirm}
          placement="rightTop"
          showCancel={false}
          trigger="click"
        >
          <Tooltip title={intl.formatMessage(messages.download)} trigger="hover">
            <FontAwesomeIcon className={styles.downloadIcon} icon={faDownload} />
          </Tooltip>
        </Popconfirm>
      </div>
      <div>
        {canCreateTerritories && (
          <>
            {showCreateConfirmation ? (
              <Popconfirm
                cancelButtonProps={{ className: styles.confirmationCancel }}
                cancelText={intl.formatMessage(messages.addConfirmationCancel)}
                description={intl.formatMessage(
                  mode === MapMode.TERRITORIES
                    ? messages.addConfirmationTerritory
                    : messages.addConfirmationGroup
                )}
                okText={intl.formatMessage(messages.addConfirmationOk)}
                onConfirm={onCreateTerritory}
                overlayClassName={styles.confirmation}
                placement="right"
                zIndex={TOPMOST_MODAL_ZINDEX}
              >
                <AddOrCreateButton
                  dataAttributes={{ "data-action-id": "createTerritory" }}
                  label={intl.formatMessage(messages.addTerritory)}
                />
              </Popconfirm>
            ) : (
              <AddOrCreateButton
                dataAttributes={{ "data-action-id": "createTerritory" }}
                label={intl.formatMessage(messages.addTerritory)}
                onClick={onCreateTerritory}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  mode: getMapMode(state),
});

export default connect(mapStateToProps, {})(Header);
